'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
let visualViewportSubscriberCount = 0;
let detachVisualViewportListeners: (() => void) | undefined;
function attachVisualViewportListeners(): () => void {
  const vv = window.visualViewport;
  if (vv == null) {
    return (): void => undefined;
  }
  function update(): void {
    const viewport = window.visualViewport;
    if (viewport == null) return;
    const offset = window.innerHeight - viewport.height - viewport.offsetTop;
    const visible = Math.max(0, Math.round(viewport.height));
    document.documentElement.style.setProperty('--keyboard-offset', `${String(Math.max(0, Math.round(offset)))}px`);
    document.documentElement.style.setProperty('--vv-visible-height', `${String(visible)}px`);
  }
  update();
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  return (): void => {
    vv.removeEventListener('resize', update);
    vv.removeEventListener('scroll', update);
    document.documentElement.style.removeProperty('--keyboard-offset');
    document.documentElement.style.removeProperty('--vv-visible-height');
  };
}

/**
 * Publishes Visual Viewport metrics on `document.documentElement` while any
 * subscriber is mounted (dialogs, bottom sheets):
 *
 * - **`--keyboard-offset`** — pixels to add to `bottom` on fixed bottom sheets.
 *   iOS ties `fixed; bottom: 0` to the layout viewport; the SW keyboard still
 *   paints over that region unless the sheet is lifted.
 * - **`--vv-visible-height`** — `visualViewport.height` for `max-height` caps.
 *   `100dvh` often does not shrink with the keyboard the way users expect.
 */
export function useVisualViewportCssVars() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
}

/**
 * Returns `true` while the on-screen software keyboard is open (i.e. the
 * visual viewport is shorter than the layout viewport). Subscribes to
 * `visualViewport.resize` + `scroll` so callers re-render on open/close.
 * SSR-safe: returns `false` until the first client effect runs.
 *
 * Consumers (e.g. `SilkSheet`) use this to compact a multi-line description
 * while the keyboard is up, freeing vertical space for the focused input.
 */
function _temp2() {
  if (typeof window === "undefined") {
    return;
  }
  visualViewportSubscriberCount = visualViewportSubscriberCount + 1;
  if (visualViewportSubscriberCount === 1) {
    detachVisualViewportListeners = attachVisualViewportListeners();
  }
  return _temp;
}
function _temp() {
  visualViewportSubscriberCount = visualViewportSubscriberCount - 1;
  if (visualViewportSubscriberCount === 0 && detachVisualViewportListeners !== undefined) {
    detachVisualViewportListeners();
    detachVisualViewportListeners = undefined;
  }
}
export function useKeyboardOpen() {
  const $ = _c(2);
  const [open, setOpen] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const vv = window.visualViewport;
      if (vv == null) {
        return;
      }
      const update = function update() {
        const viewport = window.visualViewport;
        if (viewport == null) {
          return;
        }
        const offset = window.innerHeight - viewport.height - viewport.offsetTop;
        setOpen(offset > 0);
      };
      update();
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
      return () => {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return open;
}